
import { TableCell, TableRow } from "@/components/ui/table";
import { DeleteUserDialog } from "./DeleteUserDialog";
import { User } from "./types";
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Table, TableBody, TableHead, TableHeader } from "@/components/ui/table";
import { format } from "date-fns";
import { supabase } from "@/lib/supabase";

interface UserTableRowProps {
  user: User;
  onDeleteUser: (userId: string) => Promise<void>;
}

export const UserTableRow = ({ user, onDeleteUser }: UserTableRowProps) => {
  const [showTransactions, setShowTransactions] = useState(false);

  return (
    <>
      <TableRow key={user.id}>
        <TableCell>{user.username}</TableCell>
        <TableCell>{new Date(user.created_at).toLocaleDateString()}</TableCell>
        <TableCell>
          {user.is_deleted ? (
            <span className="text-red-500">Deleted</span>
          ) : (
            <span className="text-green-500">Active</span>
          )}
        </TableCell>
        <TableCell>
          {user.is_superuser ? "Superadmin" : user.creator_status === 'creator' ? "Creator" : "User"}
        </TableCell>
        <TableCell>
          {!user.is_superuser && !user.is_deleted && (
            <DeleteUserDialog onDelete={() => onDeleteUser(user.id)} />
          )}
        </TableCell>
      </TableRow>

      <Dialog open={showTransactions} onOpenChange={setShowTransactions}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Transaction History for {user.username}</DialogTitle>
          </DialogHeader>
          <TransactionHistory userId={user.id} />
        </DialogContent>
      </Dialog>
    </>
  );
};

interface TransactionHistoryProps {
  userId: string;
}

const TransactionHistory = ({ userId }: TransactionHistoryProps) => {
  const [transactions, setTransactions] = useState<any[]>([]);

  useEffect(() => {
    const fetchTransactions = async () => {
      const { data, error } = await supabase
        .from('subscription_transactions')
        .select(`
          *,
          subscriber:subscriber_id(username),
          creator:creator_id(username)
        `)
        .or(`creator_id.eq.${userId},subscriber_id.eq.${userId}`)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching transactions:', error);
        return;
      }

      setTransactions(data || []);
    };

    fetchTransactions();
  }, [userId]);

  return (
    <div className="mt-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Amount</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Details</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {transactions.map((transaction) => (
            <TableRow key={transaction.id}>
              <TableCell>
                {format(new Date(transaction.created_at), 'MMM d, yyyy')}
              </TableCell>
              <TableCell>
                {transaction.subscription_type === 'monthly_cancel' 
                  ? 'Subscription Cancelled'
                  : `${transaction.subscription_type} Subscription`}
              </TableCell>
              <TableCell>${transaction.amount.toFixed(2)}</TableCell>
              <TableCell>
                <span className={
                  transaction.status === 'completed' 
                    ? 'text-green-500' 
                    : transaction.status === 'pending'
                    ? 'text-yellow-500'
                    : 'text-red-500'
                }>
                  {transaction.status}
                </span>
              </TableCell>
              <TableCell>
                {transaction.creator_id === userId ? (
                  <>From {transaction.subscriber?.username || 'Unknown'}</>
                ) : (
                  <>To {transaction.creator?.username || 'Unknown'}</>
                )}
              </TableCell>
            </TableRow>
          ))}
          {transactions.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} className="text-center py-4">
                No transactions found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};
